<template>
  <div
    v-if="!appLauncherDetected"
    :class="['viz-now-app', 'viz-now-app--session-true', isFullWidth && 'viz-now-app--layout-full-width']"
    session
  >
    <div v-if="contentReady" class="transitionItem">
      <loading-view v-if="loader.on" />
      <router-view v-else class="animateSimple" />

      <app-footer v-if="!hideFooter && !loader.on" />
      <arn-outdated-warning v-if="!hideFooter && !loader.on" />
      <terms v-else />
    </div>
  </div>

  <div v-else class="appLauncherContainer">
    <router-view class="animateSimple" />

    <terms hide-trigger />
  </div>
</template>

<script lang="ts">
import { computed, watch, nextTick, defineComponent } from "@vue/composition-api"
import { canJoinRoleProtectedRoute } from "@/auth/authGuard"
import { PlatformLayoutOption, PlatformViewType } from "@/types/core"
import { useStore } from "@/composable/useStore"
import { useWebview } from "@/composable/useWebview"
import { DefaultRoute } from "@/router/lib/default-routes"
import { UserRoleEnum } from "@/types/user"
import { OrganizationType } from "@/types/organization"
import router, { navigateAttemptedRoute } from "@/router"
import Router, { navigate } from "@/router"
import ArnOutdatedWarning from "@/components/arn-outdated-warning"
import AppFooter from "@/components/appFooter/AppFooterRoot.vue"
import LoadingView from "./LoadingView.vue"
import Terms from "@/components/termsAndConditions/TermsRoot.vue"
import Loader from "@/utils/loader"

export default defineComponent({
  name: "AuthParent",
  setup() {
    const { usersModule, organizationModule } = useStore()
    const { appLauncherDetected } = useWebview()
    const loader = Loader()

    const contentReady = computed(() => {
      return Boolean(usersModule.selfDetail?.id)
    })

    const hideFooter = computed(() => {
      return router.currentRoute.meta?.hideFeedbackSign
    })

    const route = computed(() => {
      return Router.currentRoute
    })

    const isFullWidth = computed(() => {
      return router.currentRoute.meta?.layout === PlatformLayoutOption.fullWidth
    })

    const organizationType = computed(() => {
      return organizationModule.organizationDetail.organizationType
    })

    const onStart = async () => {
      const isAuthenticatedRoute = route.value.meta?.requiresAuth

      if (!isAuthenticatedRoute) {
        navigateAttemptedRoute()
      }

      _validateRoutePermission()
    }

    const _assertRolePermission = () => {
      const isAdminProtected = route.value.meta?.requireAdmin
      const isInternalTool = route.value.meta?.internalTool

      if (isInternalTool && !canJoinRoleProtectedRoute(UserRoleEnum.SysAdmin)) {
        navigate({ name: DefaultRoute.ROOT })
      } else if (isAdminProtected && !canJoinRoleProtectedRoute(UserRoleEnum.Admin)) {
        navigate({ name: DefaultRoute.ROOT })
      }
    }

    const _assertViewType = () => {
      const isOperatorUser = usersModule.selfDetail.role === UserRoleEnum.ServiceUser
      const isSaasAccount = organizationModule.organizationDetail.organizationType === OrganizationType.SaaS

      if (appLauncherDetected.value || (isOperatorUser && isSaasAccount)) {
        const currentRouteType = router.currentRoute.meta?.type

        if (currentRouteType === PlatformViewType.VIZ_NOW) {
          navigate({ name: DefaultRoute.ROOT })
        }
      }
    }

    const _validateRoutePermission = () => {
      loader.run(async () => {
        await usersModule.getMyDetails()
        await organizationModule.getOrganizationDetail(usersModule.selfDetail.organizationId)

        nextTick(() => {
          _assertViewType()
          _assertRolePermission()
        })
      })
    }

    onStart()

    watch([organizationType, route], () => {
      _assertViewType()
    })

    return {
      appLauncherDetected,
      loader,
      hideFooter,
      isFullWidth,
      contentReady
    }
  },
  components: {
    Terms,
    ArnOutdatedWarning,
    AppFooter,
    LoadingView
  }
})
</script>
